'use client';

import * as React from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { Field } from './Field';
import { IS_PROD, devWarn, devWarnValueUndefined } from '../internal/devWarn';

/* Controlled-only contract: Input coerces `value ?? ''` internally,
   so a consumer that passes only `defaultValue` will find it
   ignored — the controlled branch always wins. Pass `value` +
   `onChange` for the intended usage. */
export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix' | 'className' | 'style' | 'size'
> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
  /** ADR-017 §"unsafe_*" escape hatch — inline style forwarded to the
   *  outer Field wrapper. Audit #11 (2026-05-16): renamed from `style`. */
  unsafe_style?: CSSProperties;
  inputStyle?: CSSProperties;
  /** Native input character measure, or a visual height variant routed through ADR-045 `--control-h`. */
  size?: number | 'sm' | 'md' | 'lg';
  /** ADR-031 — Input field inline sizing.
   *
   *   - `'fill'`     (default): width: 100% of the parent, preserves v5.0 behavior.
   *   - `'content'`  : `inline-size: fit-content`; releases the inner
   *                   `.j-input { flex: 1 }`. Pair with native `size={N}` on
   *                   the `<input>` to set the intrinsic measure (the
   *                   `@atelierpx/jacquard/field-width-prefers-typed-prop`
   *                   ESLint rule warns when `'content'` is used without a
   *                   paired `size`).
   *   - `'auto'`     : `inline-size: auto`, parent-driven.
   *   - `number`     : pixel value carried via `--field-inline-size` custom
   *                   property + `data-field-width-numeric` selector hook.
   *                   Min-inline-size floor enforces WCAG 2.5.5.
   *
   *  Non-px measures (`'8ch'`, `clamp(...)`, token references) stay on
   *  `unsafe_style.inlineSize` per ADR-017 coherence. The codemod at
   *  `tools/codemods/unsafe-style-to-field-width.ts` mechanically rewrites
   *  `<Input unsafe_style={{ inlineSize: N }}>` → `<Input fieldWidth={N}>`.
   *
   *  @deprecated Wrap with `<FieldGroup width="...">` instead. See ADR-032.
   *  Will be removed in v6. The prop still works in v5.2; a one-shot dev
   *  warning fires per call site (`'jkit.input.field-width-deprecated'`)
   *  and the `@atelierpx/jacquard/field-width-prefers-typed-prop` ESLint
   *  rule flags usage with an autofix where `<FieldGroup>` is in scope. */
  fieldWidth?: 'fill' | 'content' | 'auto' | number;
}

/* Dev-warn dedupe scope for invalid numeric fieldWidth values.
   Per-component-type Set mirrors devWarnValueUndefined; the first
   <Input> that gets fieldWidth={NaN} warns, every subsequent one
   is silent. Silent in production via the IS_PROD gate inside
   devWarn. */
const _seenInvalidFieldWidth = new Set<string>();
function devWarnInvalidFieldWidth(value: number): void {
  if (IS_PROD) return;
  const key = String(value);
  if (_seenInvalidFieldWidth.has(key)) return;
  _seenInvalidFieldWidth.add(key);
  devWarn(
    `[JKit.Input] received \`fieldWidth={${key}}\` (must be a finite ` +
      'positive number). The prop was dropped and the field falls back ' +
      "to the default `'fill'` width. Pass a positive finite number — or " +
      "one of `'fill' | 'content' | 'auto'` — for the intended typed path.",
  );
}

/* ── ADR-032 §2 deprecation devWarn ────────────────────
   `<Input fieldWidth>` is soft-deprecated in v5.2 (this surface):
   the prop continues to work, but a one-shot dev-warn fires per
   session keyed on `'jkit.input.field-width-deprecated'`. The
   prop is removed in v6; adopters migrate to
   `<FieldGroup width="..."><Input /></FieldGroup>` (ADR-035).

   Dedupe scope: module-level Set keyed on the stable warn-key.
   Mirrors FilterButton's `_warnedKeys` pattern + the ADR-030 A7
   devWarn matrix. Once-per-session in the browser; in SSR the
   microtask never drains before `renderToString` returns, so the
   server log stays quiet (no synchronous warn during render).

   Deferred via `queueMicrotask` so the warn does not run inside
   the render pass — synchronous warns at render time would interfere
   with React 18 strict-mode double-render and SSR. The
   `typeof queueMicrotask` guard falls back to `setTimeout` for
   environments without it (older Node, some edge runtimes). */
const _deprecationWarnedKeys = new Set<string>();
function devWarnFieldWidthDeprecated(): void {
  if (IS_PROD) return;
  const key = 'jkit.input.field-width-deprecated';
  if (_deprecationWarnedKeys.has(key)) return;
  _deprecationWarnedKeys.add(key);
  const message =
    '[JKit.Input] `fieldWidth` is deprecated. Wrap with ' +
    '<FieldGroup width="..."> instead — see ADR-032 §2. ' +
    'The prop still works in v5.2 and is removed in v6. ' +
    '(This warning fires once per session.)';
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(() => devWarn(message));
  } else {
    setTimeout(() => devWarn(message), 0);
  }
}

/** @internal Reset the deprecation-warn dedupe set. Test-only. */
export function _resetInputFieldWidthDeprecationDedupeForTest(): void {
  _deprecationWarnedKeys.clear();
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    value,
    onChange,
    type = 'text',
    placeholder,
    prefix,
    suffix,
    disabled,
    min,
    max,
    step,
    className,
    unsafe_style,
    inputStyle,
    size = 'md',
    autoFocus,
    fieldWidth,
    ...rest
  },
  ref,
) {
  /* Dev-only side-effect. Mount-only (empty deps) so the Set
     dedupe inside devWarnValueUndefined isn't consulted on every
     keystroke — a cheap no-op, but still a per-render round-trip
     for no gain. Capture value in the closure; if the consumer
     MOUNTS with undefined the warn fires once, which is the
     signal we actually care about. Consumers who start with ''
     and later flip to undefined don't hit this, but they're
     symptomatic of a different consumer bug React's own
     "controlled/uncontrolled" warning would surface. */
  React.useEffect(() => {
    if (!IS_PROD && value === undefined) devWarnValueUndefined('Input');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ADR-032 §2 deprecation — fire the one-shot dev-warn when the
     `fieldWidth` prop is read (any non-undefined value, including the
     explicit `'fill'` default). Microtask-deferred so it doesn't
     execute during render; dedupe is module-level so apps rendering
     many <Input>s only see the warn once per session. The prop still
     works — this is signal only. */
  if (!IS_PROD && fieldWidth !== undefined) devWarnFieldWidthDeprecated();

  /* ADR-031 §1-4 — fieldWidth value→attribute resolution.

     The data-attribute is the selector hook; the React layer
     emits it. The per-control default for Input is `'fill'` so
     the rendered HTML always carries a `data-field-width=...`
     attribute (the absence-as-default story would force adopters
     to read CSS specificity to predict behavior — explicit beats
     implicit here). Numeric values route through both a
     presence-only `data-field-width-numeric` attribute (selector
     hook) AND a `--field-inline-size` custom property (value
     carrier); per ADR-031 §4 routing through a custom property
     keeps the rendered measure in the cascade so adopters can
     override it with a more specific scoped rule. */
  let fieldWidthAttr: 'fill' | 'content' | 'auto' | undefined;
  let fieldWidthNumericAttr: true | undefined;
  let fieldStyle: CSSProperties | undefined = unsafe_style;
  if (typeof fieldWidth === 'number') {
    if (!Number.isFinite(fieldWidth) || fieldWidth <= 0) {
      /* Dev-warn invalid numeric value (NaN, Infinity, ≤0) and fall
         back to the default `'fill'` so the field stays renderable. */
      if (!IS_PROD) devWarnInvalidFieldWidth(fieldWidth);
      fieldWidthAttr = 'fill';
    } else {
      fieldWidthNumericAttr = true;
      /* Merge the inline custom-property carrier with any adopter-supplied
         unsafe_style. Adopter wins on collision (ADR-017 escape-hatch
         intent) — if both fieldWidth={N} and unsafe_style.inlineSize are
         provided the adopter's inline-size declaration takes precedence
         in the cascade. */
      fieldStyle = {
        ['--field-inline-size' as keyof CSSProperties]: `${fieldWidth}px`,
        ...(unsafe_style ?? {}),
      } as CSSProperties;
    }
  } else if (fieldWidth === undefined) {
    fieldWidthAttr = 'fill';
  } else {
    fieldWidthAttr = fieldWidth;
  }
  const fieldSize = typeof size === 'number' ? 'md' : size;
  const nativeInputSize = typeof size === 'number' ? size : undefined;

  return (
    <Field
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      className={className}
      size={fieldSize}
      // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix -- Input.unsafe_style forwarded to Field; both are the same escape-hatch surface
      unsafe_style={fieldStyle}
      data-field-width={fieldWidthAttr}
      data-field-width-numeric={fieldWidthNumericAttr}
    >
      <input
        {...rest}
        ref={ref}
        className="j-input"
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        size={nativeInputSize}
        autoFocus={autoFocus}
        style={inputStyle}
      />
    </Field>
  );
});
